// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
/* bootstrap 追加 */
import BootstrapVue from 'bootstrap-vue'
import './assets/style/custom-bootstrap/bootstrap.min.css'
import 'bootstrap-vue/dist/bootstrap-vue.css'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import Vue from 'vue'
import App from './App'
import router from './router'
import { store } from './store'
import Vuex from 'vuex'
import { extend } from 'vee-validate'
import { required, min } from 'vee-validate/dist/rules'
import Multiselect from 'vue-multiselect'
import VueScrollTo from 'vue-scrollto'
import FlashMessage from './common/flash-message.component'
import AppConstants from './constants/app.constants'
/* plugin 追加 */
import InfiniteLoading from 'vue-infinite-loading'
import Print from 'vue-print-nb'

// mixinsファイル
import edtableMixins from './mixins/editablePropety'
import useCaseSelection from './mixins/use-case-selection.mixin'
import HTTPClient from './common/http-client'
import 'vue2-datepicker/locale/ja'
import vClickOutside from 'v-click-outside'
import frag from 'vue-frag'
import DropdownComponent from './components/custom-components/DropdownComponent.vue'
import DropdownItemComponent from './components/custom-components/DropdownItemComponent.vue'
import DropdownGroupComponent from './components/custom-components/DropdownGroupComponent.vue'
// Google Tag Manager
import VueGtm from 'vue-gtm'
// Microsoft Entra ID (Azure AD) の設定
import msalInstance from './entra-login/entra.config'
import { EventType } from '@azure/msal-browser'

/**
 * Microsoft Entra ID (Azure AD) 認証を初期化する関数。
 */
async function initMSAL() {
  // MSALインスタンスを初期化します
  await msalInstance.initialize()
  // 認証結果を取得し、アクティブ アカウントを設定します
  if (!msalInstance.getActiveAccount()) {
    const account = JSON.parse(localStorage.getItem(AppConstants.STORAGE_ACTIVE_ACCOUNT_ENTRA))
    if (account) {
      const res = await msalInstance.acquireTokenSilent({
        scopes: ['openid', 'profile'],
        account
      }).catch(() => null)
      if (res && res.account) {
        msalInstance.setActiveAccount(res.account)
        return
      }
    }
    const result = await msalInstance.handleRedirectPromise()
    if (result && result.account) {
      // 認証結果がない場合、リダイレクトをトリガー
      msalInstance.setActiveAccount(result.account)
    } else {
      // アプリケーションに現在サインインしているすべてのユーザーアカウントを取得します
      const accounts = msalInstance.getAllAccounts()
      // 利用可能なアカウントがある場合は、配列の最初のアカウントをアクティブ アカウントとして設定します
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0])
      }
    }
  }
}

/**
 * 認証イベントを処理するためのイベントコールバックを追加する。
 */
function setupMSALEventHandlers() {
  msalInstance.addEventCallback((event) => {
    // イベントが成功したログインイベントであり、ペイロードが利用可能かどうかを確認します
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload
      const account = payload.account
      // 認証されたアカウントをアクティブアカウントとして設定する
      msalInstance.setActiveAccount(account)
      localStorage.setItem(AppConstants.STORAGE_ACTIVE_ACCOUNT_ENTRA, JSON.stringify(account))
    }
  })
}

async function init(config) {
  Vue.mixin(edtableMixins)
  Vue.mixin(useCaseSelection)
  Vue.directive('frag', frag)
  // vueコンポーネント
  Vue.component('multiselect', Multiselect)
  Vue.component('dropdown-custom', DropdownComponent)
  Vue.component('dropdown-group-custom', DropdownGroupComponent)
  Vue.component('dropdown-item-custom', DropdownItemComponent)

  // vue使用する
  Vue.use(Multiselect)
  Vue.use(BootstrapVue)
  Vue.use(Vuex)
  Vue.use(vClickOutside)
  Vue.use(InfiniteLoading, {
    props: {
      spinner: 'default'
    },
    slots: {
      error: '',
      errorBtnText: '+10件表示',
      noResults: ''
    }
  })
  Vue.use(VueScrollTo)
  Vue.use(Print)

  Vue.config.productionTip = false
  Vue.prototype.$grobalListener = new Vue()

  // FlashMessageComponentを初期化します。
  const messageComponent = FlashMessage.getInstance(
    msg => store.dispatch('flashMessage/addToSuccessMessageList', msg, { root: true }),
    msg => store.dispatch('flashMessage/addToErrorMessageList', msg, { root: true }),
    () => store.dispatch('flashMessage/removeAllErrorMessages', { root: true }),
    () => store.dispatch('flashMessage/removeAllSuccessMessages', { root: true }),
    () => store.dispatch('flashMessage/removeAllMessages', { root: true }),
    () => store.dispatch('flashMessage/removeDisconnectionErrorMessage', { root: true })
  )

  initCustomValidationRules()

  // 成功とエラーのコールバックでHttpClientを初期化します。
  Vue.prototype.$axios = HTTPClient.getInstance(
    config,
    (msg) => messageComponent.showSuccessMessage(msg),
    (msg, importMode) => messageComponent.showErrorMessage(msg, importMode)
  ).http

  // Microsoft認証ライブラリを初期化します。
  Vue.prototype.$msalInstance = msalInstance

  // ユーザー情報を取得して、ヘッダーに表示する
  setupMSALEventHandlers()

  // Microsoft Entra ID (Azure AD) 認証を初期化します
  await initMSAL()

  /* eslint-disable no-new */
  new Vue({
    el: '#app',
    router,
    components: { App },
    template: '<App/>',
    store
  })
}

function initCustomValidationRules() {
  // Extend Required rule
  extend('required', {
    ...required,
    message: '{_field_}は必須項目です。'
  })

  // Extend Email rule
  extend('email', (value) => {
    if (!value.match(/^[a-zA-Z0-9]+([_.-]?[a-zA-Z0-9]+)*@[a-zA-Z0-9]+([_.-]?[a-zA-Z0-9]+)*(.[a-zA-Z0-9]{2,4})+$/g)) {
      return '{_field_}が不正です。'
    }
    return true
  })

  // Extend Min rule
  extend('min', {
    ...min,
    message: '6～20文字を入力してください。'
  })

  // Extend ConfirmedPass rule
  extend('confirmedPass', (value, [password]) => {
    if (value !== password) {
      return '新しいパスワードと一致しません。'
    }
    return true
  }, {
    hasTarget: true
  })

  // Extend password rule
  extend('password', {
    validate: value => {
      // least 1 alphabet & number without full-width chars
      // eslint-disable-next-line no-useless-escape
      const validPasswordRegex = /[^a-zA-Z0-9\s\!\?\@\#\$\%\^\&\*\)\(+\=\.\:\/\\\[\]\{\}\,\|\"`~_<>;'-]/
      const standardPasswordRegex = /^(?=.*[a-zA-Z])(?=.*[0-9]).*$/
      if (!value.match(standardPasswordRegex)) {
        return '半角の英字と数字を混ぜてください。'
      }
      if (value.match(validPasswordRegex) ||
          value.length < 6 ||
          value > 20) {
        return '半角の英字、数字を含む6～20文字としてください。'
      }
      return true
    }
  })

  // Extend scancode search tracking item rule
  extend('scancodeRequired', {
    ...required,
    message: '{_field_}は必須です。{_field_}を入力してください。'
  })
}

// eslint-disable-next-line no-undef
Vue.prototype.$config = config
init(Vue.prototype.$config)

// Google Tag Manager タグ埋め込み
Vue.use(VueGtm, {
  id: 'GTM-WWRGXFXR'
})



// WEBPACK FOOTER //
// ./src/main.js